<template>
  <div class="product-outfits">
    <div class="product-outfits__outfit-wrapper">
      <shop-the-look
        v-if="currentOutfit"
        :key="currentOutfit"
        :source="product.handle"
        :outfit="currentOutfit"
        :main-product="product"
      />

      <div
        v-if="isMobile"
        class="product-outfits__outfit-arrows"
      >
        <button
          class="product-outfits__outfit-arrow product-outfits__outfit-arrow--prev"
          :class="{
            [$class('disabled')]: currentImageWithOutfitIndex === previousOutfit
          }"
          type="button"
          @click.prevent="goToPrevious"
        >
          <span
            class="visually-hidden"
            v-text="$string('accessibility.carousel.previous')"
          />

          <icon-arrow-left />
        </button>

        <button
          class="product-outfits__outfit-arrow product-outfits__outfit-arrow--next"
          :class="{
            [$class('disabled')]: currentImageWithOutfitIndex === nextOutfit
          }"
          type="button"
          @click.prevent="goToNext"
        >
          <span
            class="visually-hidden"
            v-text="$string('accessibility.carousel.next')"
          />

          <icon-arrow-right />
        </button>
      </div>
    </div>

    <div
      v-if="isMobile"
      class="product-outfits__outfit-thumbnails-wrapper"
    >
      <div
        :key="currentIndex"
        class="product-outfits__outfit-thumbnails"
      >
        <button
          v-for="({image, index}, imageWithOutfitIndex) of imagesWithOutfits"
          :key="index + '-' + currentImageWithOutfitIndex"
          :aria-label="image.alt"
          class="product-outfits__outfit-thumbnail"
          type="button"
          :class="{
            [$class('active')]: currentImageWithOutfitIndex === imageWithOutfitIndex
          }"
          :disabled="outfitKeys[index] === currentOutfit"
          @click.prevent="$emit('update-current-index', index)"
        >
          <responsive-image
            :image="image"
            ratio="3:4"
            sizes="(min-width: 1024px) 25vw, 50vw"
          />
        </button>
      </div>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Product outfits (product-outfits)
 * -----------------------------------------------------------------------------
 * Product outfits and shop the look section.
 *
 * @param {Number} currentIndex - The current product image index
 * @param {Array} outfitKeys - Array of outfits to load.
 * @param {Object} product - The product data
 *
 */
import { mapState } from 'vuex'

import IconArrowLeft from '~icons/directional-navigation/arrow/left-small.svg'
import IconArrowRight from '~icons/directional-navigation/arrow/right-small.svg'
import ResponsiveImage from '~global/images/responsive-image'
import ShopTheLook from '~async/shop-the-look/shop-the-look'

export default {
  name: 'ProductOutfits',

  components: {
    IconArrowLeft,
    IconArrowRight,
    ResponsiveImage,
    ShopTheLook,
  },

  props: {
    currentIndex: {
      type: Number,
      default: 0,
      required: true,
    },
    outfitKeys: {
      type: Array,
      default: () => [],
      required: true,
    },
    product: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
  },

  emits: {
    'update-current-index': () => true,
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * The index of the current outfit thumbnail
     * @returns {Number}
     */
    currentImageWithOutfitIndex() {
      let imageIndex = null

      this.imagesWithOutfits.forEach((image, index) => {
        if (image.outfit === this.currentOutfit) {
          imageIndex = index
        }
      })

      return imageIndex
    },

    /**
     * The current outfit key
     * @returns {String|null}
     */
    currentOutfit() {
      if (!this.outfitKeys?.length) {
        return null
      }

      // Look for an outfit for the current image
      let key = this.outfitKeys[this.currentIndex] || null

      // If none is found, look at previous images until a fallback is found
      if (!key && this.currentIndex > 0) {
        for (let index = this.currentIndex; index >= 0; index--) {
          if (this.outfitKeys[index]) {
            key = this.outfitKeys[index]
            break
          }
        }
      }

      // If there is still no outfit, look at next images for a fallback
      if (!key && this.currentIndex < this.outfitKeys.length) {
        for (let index = this.currentIndex; index < this.outfitKeys.length; index++) {
          if (this.outfitKeys[index]) {
            key = this.outfitKeys[index]
            break
          }
        }
      }

      return key
    },

    /**
     * The product images that have outfits
     * @returns {Array}
     */
    imagesWithOutfits() {
      const outfitsFound = []
      const images = []

      this.product?.media?.forEach(({ image }, index) => {
        const altText = image?.raw_alt || image?.alt

        if (!altText) {
          return
        }

        let outfit = null
        const firstSplit = altText?.split('{{ outfit:')
        const secondSplit = firstSplit?.[1]?.split(' }}')

        if (!secondSplit?.[1]) {
          return
        }

        outfit = secondSplit[0].trim()

        if (!outfitsFound.includes(outfit)) {
          outfitsFound.push(outfit)
          images.push({
            index,
            image,
            outfit,
          })
        }
      })

      return images
    },

    /**
     * The next outfit thumbnail index
     * @returns {Number}
     */
    nextOutfit() {
      if (this.currentImageWithOutfitIndex < this.imagesWithOutfits.length - 1) {
        return this.currentImageWithOutfitIndex + 1
      }

      return this.imagesWithOutfits.length - 1
    },

    /**
     * The previous outfit thumbnail index
     * @returns {Number}
     */
    previousOutfit() {
      if (this.currentImageWithOutfitIndex > 0) {
        return this.currentImageWithOutfitIndex - 1
      }

      return 0
    },
  },

  methods: {

    /**
     * Updates the parent component current index to the next outfit image index
     */
    goToNext() {
      if (!Number.isInteger(this.imagesWithOutfits[this.nextOutfit].index)) {
        return
      }

      this.$emit('update-current-index', this.imagesWithOutfits[this.nextOutfit].index)
    },

    /**
     * Updates the parent component current index to the prev outfit image index
     */
    goToPrevious() {
      if (!Number.isInteger(this.imagesWithOutfits[this.previousOutfit].index)) {
        return
      }

      this.$emit('update-current-index', this.imagesWithOutfits[this.previousOutfit].index)
    },
  },
}
</script>

<style lang="scss">
@import './product-outfits';
</style>
